// @ts-check
// Panel "Autocorregir ejercicio" (TSK-0124, CAP-STRUCT-CORRECTION-OUTPUT-DIAGRAM,
// DEC-041/DEC-051). Séptimo slot del editor (ver `editor-controller.js`),
// hermano de `execution-panel.js` (TSK-0058, "done", NO se modifica ni se
// importa -- docs/03-frontend.md §4.2, "componentes hermanos no se importan
// entre sí": cualquier utilidad compartida, p.ej. formatear un `Valor`
// legible, se duplica aquí en su forma mínima, mismo precedente ya asumido
// por `editor-controller.js` con `PATRON_IDENTIFICADOR`).
//
// Diferencia de fondo con "Ejecutar" (`execution-panel.js`): "Ejecutar" es
// una ejecución efímera y CLIENTE-decidida (DEC-025, nunca persiste nada, el
// alumno decide cuándo/con qué valores). "Autocorregir" es una mutación
// AUTENTICADA server-validada (DEC-051): compara la salida del programa del
// alumno contra la solución de referencia para los casos de prueba visibles
// del ejercicio EN EL SERVIDOR, incrementa `intentos` (penaliza, DEC-041) y,
// si acierta, marca el ejercicio completado -- el cliente nunca puede
// auto-marcarse completado (DEC-048). Por eso este panel SÍ necesita
// `ejercicioId` (UUID) + `csrfToken`, que "Ejecutar" nunca necesitó.
//
// ## De dónde salen `ejercicioId`/`csrfToken` (decisión de diseño)
// `editor-controller.js` documenta como "regla no negociable" (DECISIÓN 9 en
// su cabecera) que NO importa `state/**` ni `services/**`: sigue siendo una
// FEATURE reutilizable que "funciona SIN cuenta". Autocorregir, por
// contrato de backend (TSK-0122: `get_current_user` + `verify_csrf` +
// `ejercicio_id` real), es intrínsecamente una operación CON cuenta y CON
// ejercicio cargado -- no tiene sentido "sin cuenta". En vez de romper esa
// regla no negociable haciendo que ESTE panel importe `state/session-store.js`
// directamente, se seguye EXACTAMENTE el patrón ya aprobado del interruptor
// de tema (DEC-044/TSK-0108, ver `pages/shell-nav.js`): el token CSRF (y,
// aquí, además el id de ejercicio) se RESUELVEN en la capa `pages/state`
// (`diagram-editor-page.js`, que ya importa `session-store.js` para la
// guardia de sesión de TSK-0085, y ya conoce `ejercicio.id` -- ver
// `EjercicioPublico.id`, `exercise-client.js` -- tras cargar el enunciado de
// TSK-0054) y se PASAN como datos/opciones opacos hasta este panel a través
// de `editor-controller.js` (que se limita a reenviarlos, igual que ya
// reenvía `usuario`/`onCerrarSesion`, sin interpretarlos). Este módulo SÍ
// importa `services/autocorrection-client.js`/`services/attempts-client.js`
// directamente (arquitectura permitida: "features → components + services +
// state + validation + utils", docs/03-frontend.md §4.1) -- igual que
// `execution-panel.js` ya importa `diagram-execution-client.js` -- porque la
// llamada de red en sí misma es responsabilidad de este panel (qué mostrar en
// cada éxito/error), no de la página.
//
// `ejercicioId` puede no estar disponible todavía en el instante en que este
// panel se MONTA por primera vez: la carga del ejercicio
// (`obtenerEjercicioPorSlug`, TSK-0054) es asíncrona y se lanza en paralelo
// al montaje del editor. Por eso `ejercicioId` es SIEMPRE `string | null` (no
// se asume presente) y el panel se muestra deshabilitado con un mensaje
// explícito hasta que `editor-controller.js#establecerEjercicioActual`
// (llamado por la página cuando el `fetch` del ejercicio resuelve) lo
// actualice.
//
// ## Flujo de intento server-authoritative (TSK-0176, DEC-063/DEC-064/DEC-065)
// Tres botones nuevos, en este orden de dependencia:
//   1. "Iniciar intento" -- crea (o recupera idempotentemente, DEC-064 punto
//      4) el `IntentoEjercicio` abierto en el SERVIDOR (`attempts-client.js
//      #iniciarIntento`, SIN enviar ningún timestamp del cliente: el `inicio`
//      lo fija el servidor con su propio reloj). Habilita "Pista" y
//      "Corrección" y (re)arranca el cronómetro VISUAL (`tiempoInicioActual =
//      Date.now()` del cliente) -- ese cronómetro es SIEMPRE informativo, ver
//      "Indicador de tiempo" más abajo; el tiempo OFICIAL de cada intento lo
//      calcula el servidor (`duracion_segundos`, DEC-064 punto 6) y se
//      muestra al recibir el resultado de "Corrección" (`mostrarResultado`).
//   2. "Pista" -- DESHABILITADO (atributo `disabled` real, con explicación
//      textual mediante `aria-describedby`) hasta iniciar un intento.
//      Pedirla RESTA un 10% fijo del XP máximo del ejercicio una sola vez por
//      intento (DEC-063 punto 3): por eso, antes de mostrar el texto de la
//      pista, se exige una CONFIRMACIÓN explícita de la persona usuaria. Se
//      reutiliza el patrón de confirmación en dos pasos YA aprobado en
//      `block-operations.js` ("Eliminar" -> "Confirmar eliminar"/"Cancelar",
//      sin diálogo modal, sin foco atrapado): el primer clic en "Pedir pista"
//      arma la confirmación (sustituye el botón por "Confirmar pista"/
//      "Cancelar" y anuncia el aviso por la live region); solo el segundo
//      clic ("Confirmar pista") llama de verdad a `pedirPista`. Idempotente:
//      si el intento ya tenía `pistaSolicitada` (bien porque "Iniciar
//      intento" recuperó un intento abierto previo, bien porque ya se pidió
//      en esta sesión), el botón queda deshabilitado con el motivo "Ya has
//      pedido la pista de este intento" -- el servidor ya garantiza que no se
//      penaliza dos veces (DEC-063 punto 3), este panel solo refleja el
//      estado, no reimplementa esa garantía.
//   3. "Corrección" -- (antes "Autocorregir": mismo botón, mismo flujo de
//      `autocorregirDiagrama`, SOLO renombrado para casar con el vocabulario
//      de DEC-063/DEC-064/DEC-065) DESHABILITADO hasta iniciar un intento. El
//      body enviado sigue siendo `{ programa }` -- **nunca** `intentoId`: el
//      endpoint de autocorrección es `extra="forbid"` (lo rechazaría con
//      422) y el servidor correlaciona por el ÚNICO intento ABIERTO del
//      usuario para este ejercicio (DEC-064 punto 4), no por un id que el
//      cliente le pasara. Al corregir, el servidor CIERRA ese intento
//      (`fin`/`duracion_segundos` server-authoritative, DEC-064 punto 6) y
//      la respuesta añade `puntuacionObtenida`/`penalizacionPista`/
//      `duracionSegundos` (DEC-065) que este panel muestra junto al
//      veredicto. Tras corregir, ya no hay intento abierto: este panel
//      vuelve a deshabilitar "Pista"/"Corrección" y a habilitar "Iniciar
//      intento" (corregir de nuevo exige un intento NUEVO; si la persona
//      usuaria lo intentara sin iniciar uno, el servidor respondería 409, que
//      `mensajeDeErrorAutocorreccion` traduce de forma genérica pero
//      correcta -- ver esa función, `ApiError` no transporta el detalle del
//      cuerpo de la respuesta para distinguir "no autocorregible" de "sin
//      intento abierto").
//
// ## Indicador de tiempo (DEC-041/DEC-051/DEC-063): se muestra, nunca
// penaliza ni se persiste (el cliente)
// `tiempoInicioMs` (inicial) / `tiempoInicioActual` (mutable, ver arriba) es
// una marca de tiempo del CLIENTE (`Date.now()`) que aproxima "cuándo se
// inició este intento" -- puramente VISUAL, para dar feedback inmediato sin
// esperar a la respuesta del servidor. Un reloj en cliente
// (`globalThis.setInterval`, mismo patrón de escape de `no-undef` ya usado
// por `execution-panel.js#globalThis.queueMicrotask` -- `setInterval`/
// `clearInterval` no están en los globals declarados de `eslint.config.js`,
// fuera de las rutas permitidas de esta tarea) actualiza cada segundo un
// indicador "mm:ss". NUNCA se envía al backend (ni `iniciarIntento` ni
// `autocorregirDiagrama` aceptan ningún campo de tiempo, ver
// `services/attempts-client.js`/`services/autocorrection-client.js`) y NUNCA
// se persiste en `localStorage`/en ningún sitio. El tiempo OFICIAL de cada
// intento lo calcula EXCLUSIVAMENTE el servidor a partir de sus propios
// timestamps (`inicio`/`fin`, DEC-063/DEC-064 punto 6) y se muestra tal cual
// llega en `duracionSegundos` al recibir el resultado de "Corrección".
// Deliberadamente sin `aria-live` en el cronómetro visual: un lector de
// pantalla anunciando un número cada segundo es un antipatrón de
// accesibilidad reconocido (ruido constante, WAI-ARIA APG "Live Regions"
// recomienda reservarlas para cambios que de verdad necesiten anunciarse);
// el tiempo es SIEMPRE consultable (visible, y en el propio veredicto tras
// corregir) pero solo se "escucha" si la persona usuaria decide navegar
// hasta él.

// ## ACTUALIZACIÓN (petición del usuario): fila de botones, modales y
// persistencia del intento
//   - Los tres botones ("Iniciar intento", "Pedir pista", "Corrección") van
//     en UNA fila (con `flex-wrap`). Se han eliminado los párrafos de ayuda
//     por botón, el párrafo introductorio y la nota del cronómetro; el motivo
//     por el que un botón está deshabilitado se conserva de forma accesible en
//     su atributo `title` (sin alterar su nombre accesible, que sigue siendo
//     el texto del botón).
//   - La CONFIRMACIÓN de "Pedir pista" (aviso de −10% XP) ya no se hace con
//     botones en línea, sino en una VENTANA EMERGENTE (`components/
//     modal-dialog.js`). El texto de la pista, en cambio, se sigue mostrando
//     en línea, como hasta ahora.
//   - El RESULTADO de la corrección (veredicto + puntuación + casos) se muestra
//     en una VENTANA EMERGENTE.
//   - El estado del intento (id + `inicio` del servidor + `pistaSolicitada`) se
//     PERSISTE en `localStorage` (`attempt-persistence.js`) al iniciarlo, y se
//     RESTAURA al montar: así, al refrescar la página, el cronómetro sigue
//     corriendo (ahora derivado del `inicio` OFICIAL del servidor, no de
//     `Date.now()` del cliente) y el intento sigue abierto. El tiempo/puntuación
//     OFICIALES siguen siendo server-authoritative; esto es solo la mínima
//     sesión de intento del cliente. Al corregir (el servidor cierra el
//     intento) se limpia lo persistido.
import { esNodo } from './editor-model.js';
import { serializarPrograma } from './ir-serialization.js';
import { autocorregirDiagrama } from '../../services/autocorrection-client.js';
import { iniciarIntento, pedirPista } from '../../services/attempts-client.js';
import { ApiError } from '../../services/api-errors.js';
import { crearModalDialogo } from '../../components/modal-dialog.js';
import { guardarIntento, leerIntento, limpiarIntento } from './attempt-persistence.js';
import { crearAyudaGraduada } from '../../components/graded-feedback.js';
import { MENSAJE_SIN_INTENTOS, construirNivelesAyuda } from '../attempt-feedback.js';
import { mensajeDePuntuacion } from '../../utils/mensaje-puntuacion.js';

/**
 * Material pedagógico público del ejercicio (`contenido_pedagogico`, DEC-015),
 * ya mapeado a camelCase por `services/exercise-client.js`.
 * @typedef {{ objetivos?: string[], conceptosPrevios?: string[], erroresFrecuentes?: string[] }} MaterialEjercicio
 */

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 * @typedef {import('../../services/autocorrection-client.js').ResultadoAutocorreccion} ResultadoAutocorreccion
 * @typedef {import('../../services/autocorrection-client.js').CasoAutocorreccion} CasoAutocorreccion
 * @typedef {import('../../services/attempts-client.js').IntentoIniciado} IntentoIniciado
 * @typedef {import('../../services/attempts-client.js').PistaSolicitada} PistaSolicitada
 */

let contadorInstancias = 0;

/**
 * Igual criterio que `execution-panel.js#formatearValorEjecucion`, duplicado
 * aquí a propósito (ver cabecera, "componentes hermanos no se importan entre
 * sí"): representación legible de un `Valor` del motor (entero, decimal,
 * texto, booleano, lista, nulo).
 * @param {unknown} valor
 * @returns {string}
 */
export function formatearValorAutocorreccion(valor) {
  if (valor === null || valor === undefined) return 'nulo';
  if (typeof valor === 'boolean') return valor ? 'verdadero' : 'falso';
  if (Array.isArray(valor)) return valor.map(formatearValorAutocorreccion).join(', ') || '(sin valores)';
  return String(valor);
}

/**
 * Texto visible de uno de los tres campos de un caso corregido
 * (`entrada`/`salidaEsperada`/`salidaObtenida`), que el servidor entrega ya
 * como cadena formateada (`ResultadoCasoAutocorreccion`, todos `str`). Un
 * campo vacío se rotula con `respaldo` en vez de dejar la línea a medias
 * ("Salida obtenida: " y nada más), con el MISMO vocabulario que el modal de
 * "Enviar solución" (`diagram-editor-page.js`: `(sin salida)`): el alumno lee
 * la misma frase para la misma situación se mire donde se mire.
 * @param {string} texto
 * @param {string} respaldo
 * @returns {string}
 */
export function formatearCampoCaso(texto, respaldo) {
  const limpio = typeof texto === 'string' ? texto.trim() : '';
  return limpio.length > 0 ? limpio : respaldo;
}

/**
 * Formatea una duración en milisegundos como "mm:ss" (o "hh:mm:ss" si supera
 * la hora, para no truncar silenciosamente una sesión larga). Nunca negativo
 * (defensivo ante un reloj de sistema que retrocediera).
 * @param {number} milisegundos
 * @returns {string}
 */
export function formatearDuracion(milisegundos) {
  const totalSegundos = Math.max(0, Math.floor(milisegundos / 1000));
  const horas = Math.floor(totalSegundos / 3600);
  const minutos = Math.floor((totalSegundos % 3600) / 60);
  const segundos = totalSegundos % 60;
  const mm = String(minutos).padStart(2, '0');
  const ss = String(segundos).padStart(2, '0');
  return horas > 0 ? `${horas}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * Traduce un error del flujo de corrección/intento a un mensaje en español
 * apto para la interfaz, nunca el mensaje técnico crudo (docs/03-frontend.md
 * §8.4/§9.1). Se reutiliza para los tres servicios (`iniciarIntento`,
 * `pedirPista`, `autocorregirDiagrama`): comparten el mismo vocabulario de
 * error (401/403/404/409/422/red), y `ApiError` no distingue de qué endpoint
 * viene el fallo.
 * @param {unknown} error
 * @returns {string}
 */
export function mensajeDeErrorAutocorreccion(error) {
  if (error instanceof ApiError) {
    if (error.kind === 'unauthorized') {
      return 'Tu sesión ha expirado. Vuelve a iniciar sesión para poder continuar.';
    }
    if (error.kind === 'forbidden') {
      return 'No se ha podido verificar tu sesión (token de seguridad no válido). Recarga la página e inténtalo de nuevo.';
    }
    if (error.kind === 'not_found') {
      return 'No se ha encontrado este ejercicio (o este intento) en el servidor. Recarga la página e inténtalo de nuevo.';
    }
    if (error.kind === 'conflict') {
      // Un 409 significa varias cosas distintas según el endpoint ("este
      // ejercicio no admite autocorrección automática", "no tienes ningún
      // intento iniciado" o, desde 2026-08-07, "ya has completado este
      // ejercicio"). El servidor SÍ las distingue y manda un `title` pensado
      // para leerlo el alumno: se usa ese cuando viene, y solo se cae al
      // mensaje genérico si no. Antes, con tres significados y una sola
      // frase, el texto llegaba a decir algo falso -- "pulsa Iniciar intento"
      // a quien no puede porque ya lo tiene hecho.
      if (error.title) return error.title;
      return 'No se puede completar esta acción ahora mismo: puede que este ejercicio no admita autocorrección automática, o que no tengas ningún intento iniciado. Si no lo has hecho, pulsa «Iniciar intento» y vuelve a intentarlo.';
    }
    if (error.kind === 'validation_error') {
      return 'El diagrama no se puede autocorregir tal como está: revisa que todos los bloques tengan su contenido completo y vuelve a intentarlo.';
    }
    if (error.kind === 'timeout' || error.kind === 'network_error') {
      return 'No se ha podido conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo.';
    }
    return 'No se ha podido completar la operación en el servidor. Inténtalo de nuevo en unos instantes.';
  }
  return 'No se ha podido completar la operación. Inténtalo de nuevo.';
}

/**
 * Crea el panel "Autocorregir ejercicio".
 *
 * @param {{
 *   root?: HTMLElement,
 *   programa?: NodoModelo,
 *   ejercicioId?: string | null,
 *   csrfToken?: string | null,
 *   tiempoInicioMs?: number,
 *   material?: MaterialEjercicio,
 *   ejercicioCompletado?: boolean,
 *   onEjercicioCompletado?: () => void,
 *   onCambioIntento?: (hayIntentoAbierto: boolean) => void,
 *   autocorregir?: typeof autocorregirDiagrama,
 *   iniciarIntento?: typeof iniciarIntento,
 *   pedirPista?: typeof pedirPista,
 * }} [opciones] `ejercicioId`/`csrfToken` ver "De dónde salen" en la cabecera
 *   del archivo -- opacos para este panel, resueltos por
 *   `pages/diagram-editor-page.js`. `tiempoInicioMs` ver "Indicador de
 *   tiempo" -- por defecto `Date.now()` (uso aislado en tests/standalone).
 *   `ejercicioCompletado` arranca el panel sabiendo que este ejercicio ya
 *   está superado: "Iniciar intento" nace deshabilitado, sin tener que
 *   pulsarlo para que el servidor lo rechace con un 409.
 *   `onEjercicioCompletado` se invoca cuando una corrección acierta y cierra
 *   el ejercicio: quien monta el panel aprovecha para recargar la lista, que
 *   acaba de quedarse desfasada (el siguiente ejercicio ya está desbloqueado).
 *   `autocorregir`/`iniciarIntento`/`pedirPista` inyectables únicamente para
 *   tests (mismo motivo que `opciones.cargarEjerciciosDeBloque` en
 *   `diagram-editor-page.js`): nunca se sobrescriben en producción, donde
 *   siempre son las funciones reales de servicio.
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
export function crearPanelAutocorreccion(opciones = {}) {
  const programa = opciones.programa;
  const ejercicioId = typeof opciones.ejercicioId === 'string' && opciones.ejercicioId.trim() !== '' ? opciones.ejercicioId : null;
  const csrfToken = typeof opciones.csrfToken === 'string' ? opciones.csrfToken : '';
  let tiempoInicioActual = typeof opciones.tiempoInicioMs === 'number' ? opciones.tiempoInicioMs : Date.now();
  const autocorregir = opciones.autocorregir ?? autocorregirDiagrama;
  const iniciarIntentoServicio = opciones.iniciarIntento ?? iniciarIntento;
  const pedirPistaServicio = opciones.pedirPista ?? pedirPista;
  // Material pedagógico PÚBLICO del ejercicio (`contenido_pedagogico`,
  // DEC-015), reenviado por `editor-controller.js#establecerEjercicioActual`.
  // Solo alimenta la ayuda graduada determinista (§10): nunca contiene la
  // solución de referencia, la rúbrica ni casos ocultos (DEC-016).
  /** @type {MaterialEjercicio} */
  const material = opciones.material ?? {};

  contadorInstancias += 1;
  const idInstancia = contadorInstancias;
  const idTitulo = `autocorrection-panel-heading-${idInstancia}`;

  let destruido = false;
  /** @type {AbortController | null} */
  let controladorEnCurso = null;
  /** @type {AbortController | null} */
  let controladorIniciarEnCurso = null;
  /** @type {AbortController | null} */
  let controladorPistaEnCurso = null;
  /** @type {ReturnType<typeof globalThis.setInterval> | null} */
  let intervaloReloj = null;

  // ---- estado del flujo de intento (TSK-0176, DEC-063/DEC-064) ----
  /** @type {string | null} */
  let intentoIdActual = null;
  let pistaSolicitadaIntento = false;
  // Último resultado de corrección mostrado (petición del usuario): permite
  // volver a CONSULTARLO reabriendo la ventana emergente sin reenviar nada al
  // servidor. Se limpia al iniciar un intento nuevo.
  /** @type {ResultadoAutocorreccion | null} */
  let ultimoResultado = null;

  // Restauración tras un refresco (persistencia local, ver cabecera): si hay
  // un intento guardado para este ejercicio, se recupera su id, si ya se pidió
  // la pista y -- clave -- el `inicio` OFICIAL del servidor, del que se deriva
  // el cronómetro (así sigue corriendo tras recargar, sin depender del reloj
  // del cliente). El servidor sigue siendo la autoridad: si el intento ya se
  // hubiera cerrado, la corrección responderá 409, traducido a un error.
  const intentoPersistido = ejercicioId !== null ? leerIntento(ejercicioId) : null;
  if (intentoPersistido) {
    intentoIdActual = intentoPersistido.intentoId;
    pistaSolicitadaIntento = intentoPersistido.pistaSolicitada;
    tiempoInicioActual = Date.parse(intentoPersistido.inicio);
  }

  // ---- estructura DOM ----
  const seccion = document.createElement('section');
  seccion.className = 'autocorrection-panel ui-panel';
  seccion.setAttribute('aria-labelledby', idTitulo);

  const titulo = document.createElement('h2');
  titulo.id = idTitulo;
  titulo.className = 'autocorrection-panel-heading ui-panel-titulo ui-etiqueta';
  titulo.textContent = 'Autocorregir ejercicio';

  // Indicador de tiempo (ver "Indicador de tiempo" en la cabecera):
  // puramente visual, sin aria-live -- se anuncia el bloque de resultado
  // completo (que SÍ incluye el tiempo como texto, ver `mostrarResultado`)
  // cuando de verdad hay algo nuevo que anunciar.
  const etiquetaTiempo = document.createElement('span');
  etiquetaTiempo.className = 'autocorrection-panel-time-label';
  etiquetaTiempo.textContent = 'Tiempo en este intento: ';

  const valorTiempo = document.createElement('span');
  valorTiempo.className = 'autocorrection-panel-time-value ui-temporizador';
  // Sin intento (aún) el cronómetro está en 00:00; con un intento restaurado
  // tras un refresco, refleja ya el tiempo transcurrido desde su `inicio`.
  valorTiempo.textContent =
    intentoIdActual === null ? formatearDuracion(0) : formatearDuracion(Date.now() - tiempoInicioActual);

  const tiempo = document.createElement('p');
  tiempo.className = 'autocorrection-panel-time';
  tiempo.append(etiquetaTiempo, valorTiempo);

  // Disponibilidad de base (ver "De dónde salen ejercicioId/csrfToken" en la
  // cabecera): sin ejercicio identificado o sin token CSRF vigente, NINGUNO
  // de los tres botones puede activarse (mejor que dejarlos pulsables para
  // fallar siempre con el mismo error evitable), y se anuncia el motivo
  // concreto.
  const puedeOperar = ejercicioId !== null && csrfToken !== '';

  /**
   * ¿El alumno ya acertó este ejercicio? Un ejercicio completado NO se repite
   * (petición del usuario, 2026-08-07): el servidor lo rechaza con un 409 y
   * aquí, además, se deshabilita "Iniciar intento" para que ni siquiera haya
   * que pulsarlo y esperar. Empieza en lo que diga quien monta el panel y se
   * pone a `true` en cuanto una corrección sale bien.
   */
  let ejercicioCompletado = opciones.ejercicioCompletado === true;

  /** @returns {string | null} */
  function motivoBaseNoDisponible() {
    if (ejercicioId === null) {
      return 'Este editor no está asociado a ningún ejercicio todavía: la corrección no está disponible.';
    }
    if (csrfToken === '') {
      return 'Tu sesión no se ha podido verificar: la corrección no está disponible.';
    }
    if (ejercicioCompletado) {
      // Vale para los dos motivos por los que un ejercicio deja de puntuar:
      // completarlo o gastar los intentos que sumaban (2026-09-12).
      return 'Este ejercicio ya no suma puntos: pulsa «Practicar» para repasarlo.';
    }
    return null;
  }

  // ---- Los tres botones, en una sola fila (ver "ACTUALIZACIÓN" en cabecera).
  // Ya no llevan párrafo de ayuda: el motivo de deshabilitado se conserva en
  // `title` (ver `actualizarDisponibilidadBotones`), sin cambiar su nombre
  // accesible.
  const botonIniciar = document.createElement('button');
  botonIniciar.type = 'button';
  botonIniciar.className = 'autocorrection-panel-submit ui-button ui-button-primario';
  botonIniciar.textContent = 'Iniciar intento';

  const botonPista = document.createElement('button');
  botonPista.type = 'button';
  botonPista.className = 'autocorrection-panel-submit ui-button ui-button-secundario';
  botonPista.textContent = 'Pedir pista';

  const botonCorreccion = document.createElement('button');
  botonCorreccion.type = 'button';
  botonCorreccion.className = 'autocorrection-panel-submit ui-button ui-button-peligro';
  botonCorreccion.textContent = 'Corrección';

  /** Texto de la pista ya solicitada (role="status": se muestra una única vez por acción deliberada, no es un contador continuo). */
  const pistaResultado = document.createElement('p');
  pistaResultado.className = 'autocorrection-panel-status autocorrection-panel-hint-result';
  pistaResultado.setAttribute('role', 'status');
  pistaResultado.setAttribute('aria-live', 'polite');
  pistaResultado.setAttribute('aria-atomic', 'true');
  pistaResultado.hidden = true;

  const grupoAcciones = document.createElement('div');
  grupoAcciones.className = 'autocorrection-panel-attempt-actions';
  // "Practicar": repasar un ejercicio YA acertado sin que cuente ni puntúe
  // (petición del usuario, 2026-08-07). Solo aparece cuando hay algo que
  // repasar; el resto del tiempo estorbaría.
  const botonPracticar = document.createElement('button');
  botonPracticar.type = 'button';
  botonPracticar.className =
    'autocorrection-panel-submit autocorrection-panel-secundario ui-button ui-button-secundario';
  botonPracticar.textContent = 'Practicar';
  botonPracticar.hidden = true;
  botonPracticar.title = 'Vuelve a hacer este ejercicio para repasar. No cuenta como intento ni suma puntos.';

  grupoAcciones.append(botonIniciar, botonPracticar, botonPista, botonCorreccion);

  /** Live region moderada (mismo patrón que `execution-panel.js#estado`), compartida por los tres flujos. */
  const estado = document.createElement('p');
  estado.className = 'autocorrection-panel-status';
  estado.setAttribute('role', 'status');
  estado.setAttribute('aria-live', 'polite');
  estado.setAttribute('aria-atomic', 'true');
  estado.textContent = motivoBaseNoDisponible() ?? '';

  /** Región de error asertiva (mismo patrón que `execution-panel.js#error`), compartida por los tres flujos. */
  const error = document.createElement('p');
  error.className = 'autocorrection-panel-error';
  error.setAttribute('role', 'alert');
  error.hidden = true;

  // ---- Ventana emergente: confirmación de "Pedir pista" (−10% XP) ----
  const modalPista = crearModalDialogo({ titulo: 'Pedir pista' });
  modalPista.elemento.classList.add('autocorrection-panel-hint-modal');
  const avisoPista = document.createElement('p');
  avisoPista.textContent =
    'Pedir la pista resta un 10% del XP máximo de este ejercicio, una sola vez por intento. ¿Quieres continuar?';
  modalPista.cuerpo.append(avisoPista);

  const botonConfirmarPista = document.createElement('button');
  botonConfirmarPista.type = 'button';
  botonConfirmarPista.className = 'autocorrection-panel-submit ui-button ui-button-primario';
  botonConfirmarPista.textContent = 'Confirmar pista (resta 10% del XP máximo)';

  const botonCancelarPista = document.createElement('button');
  botonCancelarPista.type = 'button';
  botonCancelarPista.className =
    'autocorrection-panel-submit autocorrection-panel-submit-secondary ui-button ui-button-secundario';
  botonCancelarPista.textContent = 'Cancelar pista';

  modalPista.acciones.append(botonCancelarPista, botonConfirmarPista);

  // ---- Ventana emergente: resultado de "Corrección" ----
  const modalResultado = crearModalDialogo({ titulo: 'Resultado de la corrección' });
  modalResultado.elemento.classList.add('autocorrection-panel-result-modal');

  const resultado = document.createElement('div');
  resultado.className = 'autocorrection-panel-result';

  // Veredicto: el TEXTO es la señal principal (WCAG 2.2 SC 1.4.1), el
  // `data-correcto` (CSS) es un refuerzo de color, nunca la única vía de
  // distinguir el resultado -- mismo criterio ya aprobado en
  // `execution-panel.js#resumenEjecucion`/`diagram-editor-page.js#exercise-scoring-case-label`.
  const veredicto = document.createElement('p');
  veredicto.className = 'autocorrection-panel-verdict';

  const intentosInfo = document.createElement('p');
  intentosInfo.className = 'autocorrection-panel-attempts';

  /** Puntuación obtenida en este intento (DEC-065): XP tras aplicar la penalización de pista, si procede. */
  const puntuacionInfo = document.createElement('p');
  puntuacionInfo.className = 'autocorrection-panel-score';
  puntuacionInfo.hidden = true;

  const completadoInfo = document.createElement('p');
  completadoInfo.className = 'autocorrection-panel-completed';
  completadoInfo.hidden = true;

  const tiempoResultado = document.createElement('p');
  tiempoResultado.className = 'autocorrection-panel-result-time';

  const casosTitulo = document.createElement('h3');
  casosTitulo.className = 'autocorrection-panel-cases-heading';
  casosTitulo.textContent = 'Detalle por caso de prueba';

  const listaCasos = document.createElement('ol');
  listaCasos.className = 'autocorrection-panel-cases';

  // Ayuda graduada (§10): al final del resultado, tras los casos.
  const ayudaGraduada = crearAyudaGraduada();

  resultado.append(veredicto, intentosInfo, puntuacionInfo, completadoInfo, tiempoResultado, casosTitulo, listaCasos, ayudaGraduada.elemento);
  modalResultado.cuerpo.append(resultado);

  seccion.append(
    titulo,
    tiempo,
    grupoAcciones,
    pistaResultado,
    estado,
    error,
    modalPista.elemento,
    modalResultado.elemento,
  );

  // --------------------------------------------------------------------
  // Reloj (ver "Indicador de tiempo" en la cabecera)
  // --------------------------------------------------------------------
  // El cronómetro SOLO cuenta mientras hay un intento activo (petición del
  // usuario: "el tiempo solo se inicializa al pulsar Iniciar intento"). Sin
  // intento en curso muestra "00:00"; con intento (nuevo o restaurado tras un
  // refresco) cuenta desde su `inicio`.
  function actualizarReloj() {
    valorTiempo.textContent =
      intentoIdActual === null ? formatearDuracion(0) : formatearDuracion(Date.now() - tiempoInicioActual);
  }
  intervaloReloj = globalThis.setInterval(actualizarReloj, 1000);

  // --------------------------------------------------------------------
  // Disponibilidad de los tres botones (recalculada tras cada cambio de
  // estado: iniciar/pedir pista/corregir, éxito o error)
  // --------------------------------------------------------------------
  /**
   * Fija (o limpia) el atributo `title` de un botón como MOTIVO de estar
   * deshabilitado, sin tocar su `textContent`/nombre accesible. Reemplaza a
   * los antiguos párrafos `aria-describedby` (ver "ACTUALIZACIÓN" en cabecera).
   * @param {HTMLButtonElement} boton
   * @param {string | null} motivo
   */
  function fijarMotivo(boton, motivo) {
    if (motivo) {
      boton.title = motivo;
    } else {
      boton.removeAttribute('title');
    }
  }

  function actualizarDisponibilidadBotones() {
    const motivoBase = motivoBaseNoDisponible();
    const hayIntento = intentoIdActual !== null;
    // Quien monta el panel necesita saberlo para bloquear el lienzo mientras
    // no haya intento abierto (2026-08-07): sin eso se podía construir la
    // solución entera con calma y arrancar el cronómetro justo antes de
    // corregir, con lo que el tiempo medido no significaba nada.
    if (typeof opciones.onCambioIntento === 'function') opciones.onCambioIntento(hayIntento);

    botonIniciar.disabled = !puedeOperar || hayIntento || ejercicioCompletado;
    // "Practicar" ocupa el hueco que deja "Iniciar intento" cuando el
    // ejercicio ya está superado: es la única forma de volver a abrirlo.
    botonPracticar.hidden = !(puedeOperar && ejercicioCompletado && !hayIntento);
    botonPracticar.disabled = !puedeOperar || hayIntento;
    botonIniciar.textContent = hayIntento ? 'Intento en curso' : 'Iniciar intento';
    fijarMotivo(
      botonIniciar,
      botonIniciar.disabled
        ? motivoBase ??
            (hayIntento
              ? 'Ya tienes un intento en curso: complétalo con "Corrección" para poder iniciar uno nuevo.'
              : null)
        : null,
    );

    const puedePedirPista = puedeOperar && hayIntento && !pistaSolicitadaIntento;
    botonPista.disabled = !puedePedirPista;
    fijarMotivo(
      botonPista,
      botonPista.disabled
        ? motivoBase ??
            (!hayIntento
              ? 'Inicia un intento para pedir una pista.'
              : pistaSolicitadaIntento
                ? 'Ya has pedido la pista de este intento.'
                : null)
        : null,
    );

    // "Corrección" se habilita mientras haya un intento en curso (para enviar)
    // O si ya hay un resultado mostrado (para volver a CONSULTARLO reabriendo
    // la ventana emergente, petición del usuario).
    const puedeCorregir = puedeOperar && hayIntento;
    const puedeConsultar = !hayIntento && ultimoResultado !== null;
    botonCorreccion.disabled = !puedeCorregir && !puedeConsultar;
    // El NOMBRE del botón dice lo que va a hacer. Antes se llamaba
    // "Corrección" en los dos estados y hacía cosas distintas: sin intento
    // abierto solo REABRÍA el resultado anterior. El alumno que fallaba,
    // arreglaba su diagrama y volvía a pulsar veía otra vez el fallo de
    // antes y creía que la ventana no salía o que su arreglo no contaba.
    // Lo encontró el usuario probando la aplicación (2026-08-07).
    botonCorreccion.textContent = puedeConsultar ? 'Ver resultado' : 'Corrección';
    if (botonCorreccion.disabled) {
      fijarMotivo(botonCorreccion, motivoBase ?? (!hayIntento ? 'Inicia un intento para poder enviar tu corrección.' : null));
    } else if (puedeConsultar) {
      botonCorreccion.title =
        'Vuelve a mostrar el resultado de tu última corrección. Para corregir de nuevo, pulsa «Iniciar intento».';
    } else {
      botonCorreccion.removeAttribute('title');
    }
  }
  actualizarDisponibilidadBotones();

  // --------------------------------------------------------------------
  // Detalle de un caso de prueba
  // --------------------------------------------------------------------
  /**
   * @param {CasoAutocorreccion} caso
   * @param {number} indice
   * @returns {HTMLLIElement}
   */
  function crearItemCaso(caso, indice) {
    const item = document.createElement('li');
    item.className = 'autocorrection-panel-case';
    item.dataset.correcto = String(caso.correcto);

    const etiqueta = document.createElement('p');
    etiqueta.className = 'autocorrection-panel-case-label';
    etiqueta.textContent = `Caso ${indice + 1}: ${caso.correcto ? 'Correcto' : 'Incorrecto'}`;
    item.append(etiqueta);

    const entrada = document.createElement('p');
    entrada.className = 'autocorrection-panel-case-detail';
    entrada.textContent = `Entrada: ${formatearCampoCaso(caso.entrada, '(sin entrada)')}`;
    item.append(entrada);

    const esperada = document.createElement('p');
    esperada.className = 'autocorrection-panel-case-detail';
    esperada.textContent = `Salida esperada: ${formatearCampoCaso(caso.salidaEsperada, '(sin salida)')}`;
    item.append(esperada);

    const obtenida = document.createElement('p');
    obtenida.className = 'autocorrection-panel-case-detail';
    obtenida.textContent = `Salida obtenida: ${formatearCampoCaso(caso.salidaObtenida, '(sin salida)')}`;
    item.append(obtenida);

    return item;
  }

  // --------------------------------------------------------------------
  // Manejadores
  // --------------------------------------------------------------------

  function ocultarError() {
    error.hidden = true;
    error.textContent = '';
  }

  function ocultarResultado() {
    if (modalResultado.estaAbierto()) modalResultado.cerrar();
    listaCasos.replaceChildren();
    seccion.querySelector('.autocorrection-panel-cases-empty')?.remove();
  }

  /** @param {ResultadoAutocorreccion} datos */
  function mostrarResultado(datos) {
    ultimoResultado = datos;
    seccion.querySelector('.autocorrection-panel-cases-empty')?.remove();

    veredicto.dataset.correcto = String(datos.correcto);
    veredicto.textContent = datos.correcto
      ? '● Solución correcta'
      : '● Solución incorrecta: revisa tu diagrama e inténtalo de nuevo.';

    intentosInfo.textContent =
      datos.intentos === 1
        ? 'Intento número 1. Cada intento fallido penaliza tu puntuación de este ejercicio.'
        : `Intento número ${datos.intentos}. Cada intento fallido penaliza tu puntuación de este ejercicio.`;

    // Puntuación obtenida en ESTE intento (DEC-065): solo se muestra si el
    // ejercicio tiene XP máximo fijado (si no, `puntuacionObtenida` es
    // `null` y no hay nada calculable que mostrar). En un intento fallido el
    // servidor ya devuelve 0 (ver `autocorrection_service`), así que la
    // línea sigue siendo coherente con el veredicto de arriba en vez de
    // contradecirlo.
    if (typeof datos.puntuacionObtenida === 'number') {
      puntuacionInfo.hidden = false;
      puntuacionInfo.textContent = mensajeDePuntuacion(datos) ?? '';
    } else {
      puntuacionInfo.hidden = true;
      puntuacionInfo.textContent = '';
    }

    // `completado` es el estado AGREGADO del ejercicio (`Progreso.completado_at`),
    // no el veredicto de este intento: un ejercicio ya superado hace días
    // sigue viniendo con `completado: true` aunque el intento de ahora falle.
    // Anunciarlo en ese caso le decía al alumno "¡Ejercicio completado! Se
    // desbloquea el siguiente" justo después de "Solución incorrecta". Se
    // enseña solo cuando ESTE intento es el correcto, que es cuando la frase
    // describe algo que acaba de pasar.
    if (datos.completado && datos.correcto) {
      completadoInfo.hidden = false;
      completadoInfo.textContent = '¡Ejercicio completado! Se desbloquea el siguiente ejercicio de la secuencia.';
      // A partir de aquí el ejercicio no se repite: "Iniciar intento" queda
      // deshabilitado sin esperar a que el servidor rechace la petición.
      ejercicioCompletado = true;
      actualizarDisponibilidadBotones();
      // Y se avisa a quien monta el panel: la lista de ejercicios que tiene
      // en pantalla acaba de quedarse desfasada (el siguiente ya está
      // desbloqueado). Antes había que REFRESCAR la página para verlo -- lo
      // encontró el usuario probando la aplicación (2026-08-07).
      if (typeof opciones.onEjercicioCompletado === 'function') {
        opciones.onEjercicioCompletado();
      }
    } else if (datos.yaNoPuntua && !datos.completado && !ejercicioCompletado) {
      // Este fallo gastó el último intento que sumaba puntos: como uno
      // completado, abre el siguiente ejercicio (2026-09-12). Hasta entonces
      // el alumno se quedaba sin poder avanzar.
      completadoInfo.hidden = false;
      completadoInfo.textContent = MENSAJE_SIN_INTENTOS;
      ejercicioCompletado = true;
      actualizarDisponibilidadBotones();
      if (typeof opciones.onEjercicioCompletado === 'function') {
        opciones.onEjercicioCompletado();
      }
    } else {
      completadoInfo.hidden = true;
      completadoInfo.textContent = '';
    }

    // Tiempo OFICIAL del intento cerrado (`duracionSegundos`, calculado por
    // el servidor, DEC-064 punto 6) si el servicio lo ha entregado; si no
    // (p.ej. una respuesta antigua/simulada sin ese campo), se conserva el
    // tiempo visual del cliente como aproximación, igual que antes de esta
    // tarea.
    tiempoResultado.textContent =
      typeof datos.duracionSegundos === 'number'
        ? `Tiempo oficial de este intento (calculado por el servidor): ${formatearDuracion(datos.duracionSegundos * 1000)}.`
        : `Tiempo empleado hasta esta corrección: ${formatearDuracion(Date.now() - tiempoInicioActual)} (no penaliza tu puntuación).`;

    listaCasos.replaceChildren();
    if (datos.casos.length === 0) {
      casosTitulo.hidden = true;
      const sinCasos = document.createElement('p');
      sinCasos.className = 'autocorrection-panel-cases-empty';
      sinCasos.textContent = 'El servidor no ha devuelto ningún caso de prueba con el que comparar tu solución.';
      listaCasos.hidden = true;
      resultado.insertBefore(sinCasos, ayudaGraduada.elemento);
    } else {
      casosTitulo.hidden = false;
      listaCasos.hidden = false;
      datos.casos.forEach((caso, indice) => listaCasos.append(crearItemCaso(caso, indice)));
    }

    // Ayuda GRADUADA determinista (§10, niveles 1-3): solo si es incorrecta.
    // Capa determinista (ADR-0006): coste 0, sin proveedor de IA, sin DEC-011.
    ayudaGraduada.render(
      datos.correcto
        ? []
        : construirNivelesAyuda({
            // Exactamente el mismo formateo que la tabla de casos de arriba
            // (`crearItemCaso`), para que la pista hable el mismo idioma que
            // el alumno acaba de leer ahí: si la tabla dice "480.0", la pista
            // dice "480.0". Cuando este mapeo devolvía listas vacías, la
            // pista de nivel 2 salía como "tú das (sin valores) y se espera
            // (sin valores)" -- literalmente inservible.
            casos: datos.casos.map((caso) => ({
              entrada: formatearCampoCaso(caso.entrada, '(sin entrada)'),
              salidaEsperada: formatearCampoCaso(caso.salidaEsperada, '(sin salida)'),
              salidaObtenida: formatearCampoCaso(caso.salidaObtenida, '(sin salida)'),
              correcto: caso.correcto,
            })),
            objetivos: material.objetivos,
            conceptosPrevios: material.conceptosPrevios,
            erroresFrecuentes: material.erroresFrecuentes,
          }),
      // Si ya pidió la pista de este intento, ya pagó el descuento.
      { yaPagado: pistaSolicitadaIntento },
    );

    // El resultado se presenta en la ventana emergente (petición del usuario).
    // El foco vuelve a "Corrección" al cerrarla.
    modalResultado.abrir(botonCorreccion);
  }

  // ---- "Iniciar intento" ----
  /**
   * Abre un intento. `practica` solo se pide desde el botón "Practicar", y
   * quien decide de verdad si lo es sigue siendo el SERVIDOR a partir del
   * progreso -- este parámetro nunca puede convertir en práctica un intento
   * que puntúa, así que no es una vía para esquivar la penalización.
   * @param {boolean} [practica]
   */
  async function manejarClicIniciar(practica = false) {
    if (!puedeOperar || intentoIdActual !== null) return; // defensivo: ver comentario equivalente en manejarClicCorreccion

    ocultarError();
    botonIniciar.disabled = true;
    botonIniciar.setAttribute('aria-busy', 'true');
    estado.textContent = 'Iniciando intento…';

    controladorIniciarEnCurso?.abort();
    const controlador = new AbortController();
    controladorIniciarEnCurso = controlador;

    try {
      /** @type {IntentoIniciado} */
      const datos = await iniciarIntentoServicio({
        ejercicioId: /** @type {string} */ (ejercicioId),
        csrfToken,
        signal: controlador.signal,
        practica,
      });
      if (destruido || controlador.signal.aborted) return;

      intentoIdActual = datos.intentoId;
      pistaSolicitadaIntento = datos.pistaSolicitada;
      // Un intento nuevo (o recuperado) invalida el resultado anterior: ya no
      // se puede consultar el de la corrección previa desde este intento.
      ultimoResultado = null;
      if (modalResultado.estaAbierto()) modalResultado.cerrar();
      // El texto de una pista pedida en un intento anterior no es válido
      // para este intento (recuperado o nuevo): se oculta y solo el motivo
      // ("Ya has pedido la pista de este intento") refleja el booleano.
      pistaResultado.hidden = true;
      pistaResultado.textContent = '';

      // El cronómetro se deriva ahora del `inicio` OFICIAL del servidor (no de
      // `Date.now()` del cliente): así, tras persistirlo y refrescar, sigue
      // corriendo desde el momento real de inicio del intento. Si el servidor
      // no devolviera `inicio` (respuesta antigua/simulada), se recae en el
      // reloj del cliente como aproximación.
      const inicioMs = Date.parse(datos.inicio);
      tiempoInicioActual = Number.isNaN(inicioMs) ? Date.now() : inicioMs;
      actualizarReloj();

      // Persistencia local del intento en curso (ver cabecera): permite
      // restaurar el cronómetro y el intento tras un refresco de página.
      if (ejercicioId !== null) {
        guardarIntento(ejercicioId, {
          intentoId: datos.intentoId,
          inicio: datos.inicio,
          pistaSolicitada: datos.pistaSolicitada,
        });
      }

      estado.textContent = pistaSolicitadaIntento
        ? 'Intento recuperado: ya habías pedido la pista de este intento. Ya puedes enviar tu corrección.'
        : 'Intento iniciado: ya puedes pedir una pista o enviar tu corrección.';
    } catch (excepcion) {
      if (destruido || controlador.signal.aborted) return;
      estado.textContent = '';
      error.hidden = false;
      error.textContent = mensajeDeErrorAutocorreccion(excepcion);
    } finally {
      if (!destruido && controladorIniciarEnCurso === controlador) {
        botonIniciar.removeAttribute('aria-busy');
        actualizarDisponibilidadBotones();
      }
    }
  }

  // ---- "Pedir pista": la confirmación (−10% XP) se hace en una ventana
  // emergente (petición del usuario). "Pedir pista" ABRE el modal; el texto de
  // la pista, tras confirmar, se sigue mostrando en línea.
  function armarConfirmacionPista() {
    if (botonPista.disabled) return; // defensivo: mismo criterio que el resto de manejadores
    modalPista.abrir(botonPista);
  }

  function cancelarConfirmacionPista() {
    modalPista.cerrar();
    estado.textContent = 'Petición de pista cancelada: no se ha descontado ninguna puntuación.';
  }

  async function confirmarPista() {
    if (intentoIdActual === null) return; // defensivo: no debería poder abrirse sin intento

    modalPista.cerrar();
    ocultarError();
    botonPista.setAttribute('aria-busy', 'true');
    estado.textContent = 'Pidiendo la pista…';

    controladorPistaEnCurso?.abort();
    const controlador = new AbortController();
    controladorPistaEnCurso = controlador;

    try {
      /** @type {PistaSolicitada} */
      const datos = await pedirPistaServicio({
        ejercicioId: /** @type {string} */ (ejercicioId),
        intentoId: intentoIdActual,
        csrfToken,
        signal: controlador.signal,
      });
      if (destruido || controlador.signal.aborted) return;

      pistaSolicitadaIntento = datos.pistaSolicitada;
      pistaResultado.hidden = false;
      pistaResultado.textContent = `Pista: ${datos.pista}`;
      estado.textContent = 'Pista mostrada. Recuerda que se ha descontado un 10% del XP máximo de este intento.';

      // Se persiste que ya se pidió la pista para este intento (restauración
      // tras refresco).
      if (ejercicioId !== null && intentoPersistidoVigente()) {
        guardarIntento(ejercicioId, {
          intentoId: /** @type {string} */ (intentoIdActual),
          inicio: new Date(tiempoInicioActual).toISOString(),
          pistaSolicitada: pistaSolicitadaIntento,
        });
      }
    } catch (excepcion) {
      if (destruido || controlador.signal.aborted) return;
      estado.textContent = '';
      error.hidden = false;
      error.textContent = mensajeDeErrorAutocorreccion(excepcion);
    } finally {
      if (!destruido && controladorPistaEnCurso === controlador) {
        botonPista.removeAttribute('aria-busy');
        actualizarDisponibilidadBotones();
      }
    }
  }

  /** @returns {boolean} Hay un intento en curso cuya persistencia tiene sentido actualizar. */
  function intentoPersistidoVigente() {
    return intentoIdActual !== null && !Number.isNaN(tiempoInicioActual);
  }

  // ---- "Corrección" (antes "Autocorregir") ----
  async function manejarClicCorreccion() {
    // Modo CONSULTA (petición del usuario): sin intento en curso pero con un
    // resultado ya mostrado, "Corrección" solo REABRE la ventana emergente con
    // ese último resultado -- no reenvía nada al servidor.
    if (intentoIdActual === null && ultimoResultado !== null) {
      mostrarResultado(ultimoResultado);
      // Aviso explícito: esto NO es una corrección nueva. Sin él, quien
      // acaba de arreglar su diagrama cree que lo que ve en la ventana
      // refleja su arreglo, y no es así.
      estado.textContent =
        'Este es el resultado de tu última corrección. Para corregir el diagrama que tienes ahora, pulsa «Iniciar intento».';
      return;
    }
    if (!esNodo(programa)) {
      ocultarResultado();
      error.hidden = false;
      error.textContent = 'No hay ningún diagrama que autocorregir todavía.';
      return;
    }
    if (ejercicioId === null) {
      ocultarResultado();
      error.hidden = false;
      error.textContent = 'Este editor no está asociado a ningún ejercicio todavía: la corrección no está disponible.';
      return;
    }
    if (csrfToken === '') {
      ocultarResultado();
      error.hidden = false;
      error.textContent = 'Tu sesión no se ha podido verificar. Recarga la página e inicia sesión de nuevo.';
      return;
    }
    if (intentoIdActual === null) {
      ocultarResultado();
      error.hidden = false;
      error.textContent = 'Inicia un intento antes de enviar tu corrección.';
      return;
    }

    ocultarError();
    ocultarResultado();
    botonCorreccion.disabled = true;
    botonCorreccion.setAttribute('aria-busy', 'true');
    estado.textContent = 'Enviando tu solución al servidor…';

    controladorEnCurso?.abort();
    const controlador = new AbortController();
    controladorEnCurso = controlador;

    try {
      const irJson = serializarPrograma(programa);
      // IMPORTANTE (DEC-064 punto 4): NUNCA se envía `intentoId` en este
      // body -- el endpoint de autocorrección es `extra="forbid"` (lo
      // rechazaría con 422) y el servidor correlaciona por el ÚNICO intento
      // ABIERTO del usuario para este ejercicio, no por un id que el cliente
      // le pasara.
      const datos = await autocorregir({
        ejercicioId,
        programa: irJson,
        csrfToken,
        signal: controlador.signal,
      });
      if (destruido || controlador.signal.aborted) return;
      estado.textContent = datos.correcto
        ? 'Corrección completada: solución correcta.'
        : 'Corrección completada: solución incorrecta.';
      mostrarResultado(datos);

      // El servidor cierra el intento abierto al corregir (DEC-064 punto 5):
      // ya no hay ningún intento en curso, así que se reflejan de vuelta las
      // condiciones iniciales -- "Pista"/"Corrección" vuelven a exigir un
      // "Iniciar intento" nuevo. Se limpia también la persistencia local: el
      // intento ya está cerrado, no debe restaurarse tras un refresco.
      intentoIdActual = null;
      pistaSolicitadaIntento = false;
      if (ejercicioId !== null) limpiarIntento(ejercicioId);
    } catch (excepcion) {
      if (destruido || controlador.signal.aborted) return;
      estado.textContent = '';
      error.hidden = false;
      error.textContent = mensajeDeErrorAutocorreccion(excepcion);
    } finally {
      // Mismo criterio que `execution-panel.js#ejecutarConEntradas`: solo la
      // invocación CORRIENTE reactiva el botón.
      if (!destruido && controladorEnCurso === controlador) {
        botonCorreccion.removeAttribute('aria-busy');
        actualizarDisponibilidadBotones();
      }
    }
  }

  // `manejarClicIniciar` recibe un booleano; un listener de click le pasaría
  // el EVENTO, que es "truthy" y convertiría todo intento en práctica. Por eso
  // los dos van envueltos y explícitos.
  const alPulsarIniciar = () => manejarClicIniciar(false);
  const alPulsarPracticar = () => manejarClicIniciar(true);
  botonIniciar.addEventListener('click', alPulsarIniciar);
  botonPracticar.addEventListener('click', alPulsarPracticar);
  botonPista.addEventListener('click', armarConfirmacionPista);
  botonConfirmarPista.addEventListener('click', confirmarPista);
  botonCancelarPista.addEventListener('click', cancelarConfirmacionPista);
  botonCorreccion.addEventListener('click', manejarClicCorreccion);

  // Si se restauró un intento en curso (refresco de página), refleja el estado
  // "intento en curso" en los botones y anuncia la recuperación.
  if (intentoPersistido) {
    actualizarDisponibilidadBotones();
    estado.textContent =
      'Intento en curso recuperado: el cronómetro sigue corriendo. Ya puedes pedir una pista o enviar tu corrección.';
  }

  function destruir() {
    destruido = true;
    controladorEnCurso?.abort();
    controladorIniciarEnCurso?.abort();
    controladorPistaEnCurso?.abort();
    if (intervaloReloj !== null) {
      globalThis.clearInterval(intervaloReloj);
      intervaloReloj = null;
    }
    botonIniciar.removeEventListener('click', alPulsarIniciar);
    botonPracticar.removeEventListener('click', alPulsarPracticar);
    botonPista.removeEventListener('click', armarConfirmacionPista);
    botonConfirmarPista.removeEventListener('click', confirmarPista);
    botonCancelarPista.removeEventListener('click', cancelarConfirmacionPista);
    botonCorreccion.removeEventListener('click', manejarClicCorreccion);
    modalPista.destruir();
    modalResultado.destruir();
  }

  if (opciones.root) {
    opciones.root.append(seccion);
  }

  return { elemento: seccion, destruir };
}
